import { defineStore, acceptHMRUpdate } from 'pinia'
import { required } from '@core/utils/validation'
import { useFoldersStore } from '@/stores/folders.store'
import { useChatsStore } from '@/stores/chats.store'
import { canACL } from '@/plugins/acl/checkaccess'
import { TABLE_WORKFLOWS, displayMode } from '@core/utils'

// eslint-disable-next-line import/prefer-default-export
export const useWorkflowsStore = defineStore({
  id: TABLE_WORKFLOWS,
  state: () => ({

  }),
  actions: {

    getHeader(payload, item) {
      return {
        title: 'Workflows',
        icon: 'mdi-sitemap-outline',
        name: ['name'],
        folder: payload?.folder,
        display: displayMode(TABLE_WORKFLOWS, 'table'),
        newData: 'NewWorkflow',
        actionData: 'EditWorkflows',
        editData: 'EditWorkflow',
        search: 'SearchWorkflow',
        searchIcon: 'mdi-text-search',
        add: {
          text: 'AddWorkflow',
          icon: 'mdi-plus',
        },
        actions: [{
          title: 'Save', icon: 'mdi-content-save-outline', action: 'save', loading: false, acl: 'create,update',
        }, {
          title: 'Duplicate',
          icon: 'mdi-content-duplicate',
          action: 'duplicate',
          acl: 'create',
          disabled: !item?.created_at,
        },
        {
          title: 'Delete',
          icon: 'mdi-delete-outline',
          action: 'delete',
          acl: 'delete',
          color: 'error',
          disabled: !item?.created_at,
        }],
      }
    },

    getTableOptions() {
      return {
        sortBy: ['updated_at'],
        sortDesc: [true],
        dense: false,
        multiSort: true,
        disableSort: false,
        itemsPerPage: 12,
        fixedHeader: true,
        groupDesc: [],
        groupBy: [],
        mustSort: false,
        page: 1,
        height: 0,
        filters: {},
      }
    },

    getTableColumns() {
      return [
        {
          text: 'Name',
          value: 'name',
          type: 'font-weight-semibold text-button primary--text',
          filterable: true,
          search: true,
        },
        {
          text: 'UpdatedAt',
          value: 'updated_at',
          type: 'date',
        },
        {
          text: 'Action',
          value: 'actions',
          align: 'center',
          type: 'actions',
          sortable: false,
        },
      ]
    },

    getModel(payload) {
      return {
        // general
        name: null,
        folder_id: payload?.folder_id,
        project_id: payload?.project_id,
        payload: {},
      }
    },

    getSchema(item, items, payload) {
      const foldersStore = useFoldersStore()
      const chatsStore = useChatsStore()

      const schema = {
        col1: {
          type: 'wrap',
          col: payload.isLeftSidebarOpen ? 12 : { cols: 12, md: 4 },
          flat: true,
          nobackground: true,
          schema: {},
        },
        col2: {
          type: 'wrap',
          col: payload.isLeftSidebarOpen ? 12 : { cols: 12, md: 4 },
          flat: true,
          nobackground: true,
          schema: {},
        },
        col3: {
          type: 'wrap',
          col: payload.isLeftSidebarOpen ? 12 : { cols: 12, md: 4 },
          flat: true,
          nobackground: true,
          schema: {},
        },
      }

      schema.col1.schema.general = {
        title: 'General',
        type: 'wrap',
        col: 12,
        outlined: true,
        shaped: true,
        ripple: false,
        class: 'px-3 ma-2',
        schema: {
          name: {
            type: 'text',
            label: 'Name',
            class: 'mx-1 mb-2',
            prependInnerIcon: 'mdi-database',
            rules: items.length > 0 ? [] : [required],
            hidden: !!items.length,
          },
          folder_id: {
            type: 'autocomplete',
            subtype: 'folders',
            label: 'Folders',
            class: 'mx-1 mb-2',
            col: 12,
            clearable: true,
            complex: true,
            prependInnerIcon: 'mdi-folder-open',
            dense: true,
            items: foldersStore.folders,
            rules: items.length > 0 ? [] : [required],
          },
        },
      }

      if (!canACL('view', 'folders', { id: payload?.project_id })) {
        delete schema.col1.schema.general.schema.folder_id
      }

      schema.col2.schema.workflow = {
        title: 'Editor',
        type: 'wrap',
        outlined: true,
        shaped: true,
        col: 12,
        ripple: false,
        class: 'pa-3 ma-2',
        hidden: !!items.length,
        schema: {
          workflow_editor: {
            type: 'btn',
            label: 'EditWorkflow',
            outlined: true,
            small: true,
            class: 'mb-3',
            iconLeft: 'mdi-pencil',
            color: 'primary',
            offset: 3,
            col: 6,
          },
        },
      }
      if (item?.id) {
        schema.col3.schema.details = {
          title: 'Details',
          type: 'wrap',
          outlined: true,
          shaped: true,
          col: 12,
          ripple: false,
          class: 'pa-3 ma-2',
          schema: {},
        }
        schema.col3.schema.details.schema.id = {
          type: 'text',
          label: 'ID',
          prependInnerIcon: 'mdi-key',
          disabled: true,
          col: 12,
          hidden: !!items.length,
        }
        schema.col3.schema.details.schema.created_at = {
          type: 'date-time-picker',
          label: 'CreatedAt',
          prependInnerIcon: 'mdi-pencil-plus',
          disabled: true,
          col: 12,
          hidden: !!items.length,
        }
        schema.col3.schema.details.schema.created_by = {
          type: 'select',
          subtype: 'admins',
          label: 'CreatedBy',
          disabled: true,
          'item-value': 'id',
          'item-text': 'fullname',
          hidden: !!items.length,
          col: 12,
          items: chatsStore.contacts,
        }
        schema.col3.schema.details.schema.updated_at = {
          type: 'date-time-picker',
          label: 'UpdatedAt',
          prependInnerIcon: 'mdi-pencil',
          disabled: true,
          col: 12,
          hidden: !!items.length,
        }
        schema.col3.schema.details.schema.updated_by = {
          type: 'select',
          subtype: 'admins',
          label: 'UpdatedBy',
          disabled: true,
          'item-value': 'id',
          'item-text': 'fullname',
          hidden: !!items.length,
          col: 12,
          items: chatsStore.contacts,
        }
      }

      return schema
    },

    getFiltersSchema(item, payload) {
      const schema = {
        filter: {
          title: 'Filters',
          disabled: !canACL('view', 'properties', { id: payload?.project_id }),
          type: 'wrap',
          col: 12,
          outlined: true,
          shaped: true,
          ripple: false,
          class: 'pa-3 ma-2 listing_filters',
          hidden: item?.type !== 'auto',
          schema: {
            group: {
              type: 'select',
              subtype: 'translate',
              col: { cols: 8, md: 6 },
              label: 'Condition',
              class: 'mx-1 mb-2 listing-group',
              'item-text': 'name',
              items: [
                { name: 'AND', value: 'AND' },
                { name: 'OR', value: 'OR' },
              ],
            },
            add_filter: {
              type: 'btn',
              iconLeft: 'mdi-plus',
              label: 'AddFilter',
              outlined: true,
              small: true,
              class: 'mb-2 mt-1 mx-1',
              color: 'primary',
              col: { cols: 8, md: 6 },
            },
            filters: {
              type: 'array',
              col: 12,
              schema: {
                group: {
                  type: 'select',
                  subtype: 'translate',
                  col: { cols: 8, md: 2 },
                  label: 'Condition',
                  class: 'mx-1 mb-2',
                  'item-text': 'name',
                  hidden: true,
                  items: [
                    { name: 'AND', value: 'AND' },
                    { name: 'OR', value: 'OR' },
                  ],
                  disabled: true,
                },
                add_condition: {
                  type: 'btn',
                  col: { cols: 2, md: 1 },
                  offset: 1,
                  'x-small': true,
                  outlined: true,
                  iconCenter: 'mdi-plus',
                  class: 'mb-3',
                  tooltip: 'AddCondition',
                  color: 'primary',
                },
                delete_filter: {
                  type: 'btn',
                  col: { cols: 2, md: 1 },
                  class: 'mb-3',
                  'x-small': true,
                  outlined: true,
                  iconCenter: 'mdi-delete-outline',
                  color: 'error',
                },
                conditions: {
                  type: 'array',
                  offset: 1,
                  col: 11,
                  schema: {
                    condition: {
                      type: 'select',
                      subtype: 'translate',
                      col: { cols: 12, md: 2 },
                      label: 'Condition',
                      class: 'mx-1 mb-2',
                      'item-text': 'name',
                      'item-value': 'value',
                      items: [
                        { name: 'AND', value: 'AND' },
                        { name: 'OR', value: 'OR' },
                      ],
                    },
                    property: {
                      type: 'select',
                      label: 'Property',
                      class: 'mx-1 mb-2',
                      items: payload?.properties,
                      'item-text': 'name',
                      'item-value': 'slug',
                      rules: [required],
                      col: { cols: 12, md: 3 },
                    },
                    operation: {
                      type: 'select',
                      col: { cols: 12, md: 3 },
                      label: 'Operation',
                      class: 'mx-1 mb-2',
                      'item-text': 'name',
                      items: [
                        { name: 'Starts with', value: 'starts' },
                        { name: 'Contains', value: 'contains' },
                        { name: 'Not contains', value: 'not-contains' },
                        { name: 'Equals', value: 'equals' },
                        { name: 'Not equals', value: 'not-equals' },
                      ],
                    },
                    value: {
                      label: 'Value',
                      type: 'text',
                      col: { cols: 10, md: 3 },
                      class: 'mx-1 mb-2',
                    },
                    remove_condition: {
                      type: 'btn',
                      col: { cols: 2, md: 1 },
                      class: 'mt-2',
                      'x-small': true,
                      outlined: true,
                      iconCenter: 'mdi-delete-outline',
                      color: 'error',
                    },
                  },
                },
              },
            },
          },
        },
      }

      return schema
    },

    getPropertiesSchema(item, payload) {
      const schema = {
        filter: {
          title: 'Properties',
          disabled: !canACL('view', 'properties', { id: payload?.project_id }),
          type: 'wrap',
          col: 12,
          outlined: true,
          shaped: true,
          ripple: false,
          class: 'pa-3 ma-2 listing_filters',
          hidden: item?.type !== 'auto',
          schema: {
            add_filter: {
              type: 'btn',
              iconLeft: 'mdi-plus',
              label: 'AddProperty',
              outlined: true,
              small: true,
              class: 'mb-2 mt-1 mx-1',
              color: 'primary',
              col: { cols: 8, md: 6 },
            },
            filters: {
              type: 'array',
              col: 12,
              schema: {
                conditions: {
                  type: 'array',
                  offset: 1,
                  col: 11,
                  schema: {
                    property: {
                      type: 'select',
                      label: 'Property',
                      class: 'mx-1 mb-2',
                      items: payload?.properties,
                      'item-text': 'name',
                      'item-value': 'slug',
                      rules: [required],
                      col: { cols: 12, md: 3 },
                    },
                    value: {
                      label: 'Value',
                      type: 'text',
                      col: { cols: 10, md: 3 },
                      class: 'mx-1 mb-2',
                    },
                    delete_filter: {
                      type: 'btn',
                      col: { cols: 2, md: 1 },
                      class: 'mt-2',
                      'x-small': true,
                      outlined: true,
                      iconCenter: 'mdi-delete-outline',
                      color: 'error',
                    },
                  },
                },
              },
            },
          },
        },
      }

      return schema
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useWorkflowsStore, import.meta.hot))
}
